/**
 * App version / stale-build detection.
 *
 * Problem it solves: after a deploy, the browser may keep a cached index.html
 * that references the OLD hashed bundles, so users see stale UI until they hard
 * reload or clear cache. This module detects a newer deploy and refreshes for
 * the user automatically (right after login) or via a banner (mid-session).
 *
 * Design guarantees (non-breaking):
 *   - If the version was not stamped at build time (dev / `npm start`), this is
 *     a complete no-op — the app behaves exactly as before.
 *   - If the version.json fetch fails for any reason, it is a no-op.
 *   - It NEVER unregisters service workers (Firebase messaging SW is left intact).
 *   - Auto-reload happens at most once per newly-detected version, so a
 *     misconfigured server (which keeps serving a stale index.html) can never
 *     cause an infinite reload loop.
 *      version.json is fetched with cache-bypassing headers, so a misconfigured
 */

// Version this bundle was BUILT from (inlined by CRA at build time). Undefined
// in dev, which disables the whole mechanism.
const BAKED_VERSION = process.env.REACT_APP_BUILD_VERSION || null;

// Remembers the version we've already reloaded/handled, so the message never
// re-appears for it. Stored in a COOKIE (not sessionStorage) on purpose: the
// app calls sessionStorage.clear() / localStorage.clear() in many places
// (logout, tab tracker, auto-logout, checkLogin, ...), which would otherwise
// wipe this guard and let the toast show again after a reload. A session cookie
// survives those clears and the reload, and is dropped when the browser closes.
// Note: we don't use localStorage because it is not available in some browsers
// (e.g. Safari in private mode) and would throw on access.
const RELOADED_COOKIE = 'vc_reloaded_for';

function getReloadedFor() {
    try {
        const m = (document.cookie || '').match(/(?:^|;\s*)vc_reloaded_for=([^;]+)/);
        return m ? decodeURIComponent(m[1]) : null;
    } catch (e) {
        return null;
    }
}

function setReloadedFor(version) {
    try {
        document.cookie = RELOADED_COOKIE + '=' + encodeURIComponent(version) + '; path=/; SameSite=Lax';
    } catch (e) {
        /* ignore */
    }
}

export function getBakedVersion() {
    return BAKED_VERSION;
}

/** Fetch the latest deployed version id from /version.json (cache-bypassing). */
async function fetchLatestVersion() {
    try {
        const base = process.env.PUBLIC_URL || '';
        const res = await fetch(`${base}/version.json?ts=${Date.now()}`, {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' },
        });
        if (!res || !res.ok) return null;
        const data = await res.json();
        return data && data.version ? String(data.version) : null;
    } catch (e) {
        return null;
    }
}

/**
 * Best-effort clearing of the Cache Storage API only. We intentionally do NOT
 * touch service worker registrations so push notifications keep working.
 */
async function clearCacheStorage() {
    try {
        if (typeof window !== 'undefined' && window.caches && window.caches.keys) {
            const keys = await window.caches.keys();
            await Promise.all(keys.map((k) => window.caches.delete(k).catch(() => {})));
        }
    } catch (e) {
        /* ignore */
    }
}

/** Clear caches then reload to pull the fresh index.html + hashed bundles. */
async function forceReload() {
    await clearCacheStorage();
    window.location.reload();
}

/**
 * Check whether a newer build is deployed.
 *
 * @param {Object}   opts
 * @param {boolean}  opts.autoReload  When true, reload to the new build (once
 *                                    per new version). When false, only invoke
 *                                    onStale so the caller can show a banner
 *                                    (used mid-session to protect unsaved work).
 * @param {number}   opts.delayMs     When auto-reloading, wait this long before
 *                                    reloading so onStale's toast is readable.
 * @param {Function} opts.onStale     Called with the latest version when a
 *                                    newer build exists — used to show a toast.
 *                                    In auto-reload mode it fires only when a
 *                                    reload is actually about to happen.
 * @returns {Promise<boolean>} true if a newer build was detected.
 */
export async function checkForUpdate({ autoReload = false, delayMs = 0, onStale } = {}) {
    if (!BAKED_VERSION) return false; // not stamped (dev) -> disabled
    const latest = await fetchLatestVersion();
    if (!latest) return false; // fetch failed -> behave as today
    if (latest === BAKED_VERSION) return false; // already up to date

    // A newer build exists. Act at most once for a given version so a build or
    // server that keeps reporting "new" (e.g. after a reload that didn't take)
    // can neither loop-reload nor re-nag with the banner. This guard is shared
    // by BOTH paths: once we've reloaded/handled `latest`, the message will not
    // re-appear for that same version, anywhere.
    if (getReloadedFor() === latest) return true; // already handled -> stay quiet

    if (autoReload) {
        setReloadedFor(latest);

        if (typeof onStale === 'function') onStale(latest); // show the toast first
        if (delayMs > 0) {
            setTimeout(() => { forceReload(); }, delayMs);
        } else {
            await forceReload();
        }
        return true;
    }

    // Detection-only mode (mid-session): let the caller show a banner.
    if (typeof onStale === 'function') onStale(latest);
    return true;
}

/**
 * Manual "Reload" action for the update banner. Pass the detected version so it
 * is marked as handled — otherwise, if the reload doesn't actually change the
 * running build (e.g. a stale-serving server), the banner would re-appear right
 * after the reload.
 */
export function reloadToLatest(version) {
    if (version) setReloadedFor(version);
    return forceReload();
}
